import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Contrast, Accessibility, Keyboard } from 'lucide-react';
import { useAccessibility } from '../contexts/AccessibilityContext';

// Floating accessibility toolbar
const AccessibilityToolbar = () => {
  const { highContrast, toggleHighContrast, announce } = useAccessibility();
  const [isVisible, setIsVisible] = React.useState(false);

  // Show toolbar on focus or keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Show on Alt + A
      if (e.altKey && e.key === 'a') {
        e.preventDefault();
        setIsVisible(prev => !prev);
        if (!isVisible) {
          announce('Accessibility toolbar opened', 'assertive');
        }
      }
      // Hide on Escape
      if (e.key === 'Escape' && isVisible) {
        setIsVisible(false);
        announce('Accessibility toolbar closed');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, announce]);

  const handleToggleHighContrast = () => {
    toggleHighContrast();
  };

  const handleShowKeyboardShortcuts = () => {
    announce('Keyboard shortcuts: Alt+A for accessibility toolbar, Tab to navigate, Enter to activate, Escape to close');
  };

  return (
    <>
      {/* Accessibility button trigger */}
      <Button
        onClick={() => setIsVisible(prev => !prev)}
        onFocus={() => setIsVisible(true)}
        className="fixed bottom-5 right-5 z-[1000] h-14 w-14 rounded-full shadow-lg hover:scale-110 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
        aria-label="Open accessibility options (Alt+A)"
        title="Accessibility Options (Alt+A)"
      >
        <Accessibility className="h-7 w-7" />
      </Button>

      {/* Accessibility toolbar */}
      {isVisible && (
        <Card
          className="fixed bottom-24 right-5 z-[1001] min-w-[250px] max-w-[300px] border-2 border-primary shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-200"
          role="dialog"
          aria-labelledby="accessibility-toolbar-title"
          aria-describedby="accessibility-toolbar-description"
        >
          <CardHeader className="pb-2">
            <CardTitle id="accessibility-toolbar-title" className="text-base">
              Accessibility Options
            </CardTitle>
            <CardDescription id="accessibility-toolbar-description">
              Customize your viewing experience
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <hr className="border-border" />

            <div className="space-y-2">
              {/* High Contrast Toggle */}
              <div className="flex items-center justify-between">
                <span className="text-sm">High Contrast</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={highContrast ? "default" : "ghost"}
                        size="icon"
                        onClick={handleToggleHighContrast}
                        aria-pressed={highContrast}
                        aria-label={`${highContrast ? "Disable" : "Enable"} high contrast mode`}
                        className="focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                      >
                        <Contrast className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{highContrast ? "Disable high contrast" : "Enable high contrast"}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {/* Keyboard Shortcuts */}
              <div className="flex items-center justify-between">
                <span className="text-sm">Keyboard Help</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleShowKeyboardShortcuts}
                        aria-label="Show keyboard shortcuts"
                        className="focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                      >
                        <Keyboard className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Show keyboard shortcuts</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <hr className="border-border" />

            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                Press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Alt + A</kbd> to toggle this toolbar<br />
                Press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Esc</kbd> to close
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default AccessibilityToolbar;
